import React from "react";
import { View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { z } from "zod";
import { DisplayNamesSchema, getDisplayNameDefaults } from "@/types/db/tenants";

export function ContactInfoForm({ contact, onChange }: { contact: any; onChange: (contact: any) => void }) {
  return (
    <View style={styles.formGrid}>
      <ThemedInput
        label="Primary Email"
        value={contact?.primary_email || ""}
        onChangeText={(v) => onChange({ ...contact, primary_email: v })}
        style={styles.fullWidth}
      />

      <View style={styles.row}>
        <ThemedInput
          label="Country Code"
          value={String(contact?.primary_phone?.country_code || "1")}
          onChangeText={(v) =>
            onChange({
              ...contact,
              primary_phone: { ...contact?.primary_phone, country_code: parseInt(v) || 1 },
            })
          }
          keyboardType="numeric"
          style={{ width: 100 }}
        />
        <ThemedInput
          label="Area Code"
          value={String(contact?.primary_phone?.area_code || "")}
          onChangeText={(v) =>
            onChange({
              ...contact,
              primary_phone: { ...contact?.primary_phone, area_code: parseInt(v) || 0 },
            })
          }
          keyboardType="numeric"
          style={{ width: 120 }}
        />
        <ThemedInput
          label="Phone Number"
          value={String(contact?.primary_phone?.phone || "")}
          onChangeText={(v) =>
            onChange({
              ...contact,
              primary_phone: { ...contact?.primary_phone, phone: parseInt(v) || 0 },
            })
          }
          keyboardType="numeric"
          style={{ flex: 1 }}
        />
      </View>

      <View style={styles.row}>
        <ThemedInput
          label="Street Address"
          value={contact?.primary_address?.street || ""}
          onChangeText={(v) =>
            onChange({
              ...contact,
              primary_address: { ...contact?.primary_address, street: v },
            })
          }
          style={{ flex: 2 }}
        />
        <ThemedInput
          label="Type"
          value={contact?.primary_address?.type || ""}
          onChangeText={(v) =>
            onChange({
              ...contact,
              primary_address: { ...contact?.primary_address, type: v },
            })
          }
          style={{ width: 120 }}
        />
      </View>

      <View style={styles.row}>
        <ThemedInput
          label="City"
          value={contact?.primary_address?.city || ""}
          onChangeText={(v) =>
            onChange({
              ...contact,
              primary_address: { ...contact?.primary_address, city: v },
            })
          }
          style={{ flex: 1 }}
        />
        <ThemedInput
          label="State"
          value={contact?.primary_address?.state || ""}
          onChangeText={(v) =>
            onChange({
              ...contact,
              primary_address: { ...contact?.primary_address, state: v },
            })
          }
          style={{ width: 80 }}
        />
        <ThemedInput
          label="ZIP Code"
          value={contact?.primary_address?.zip || ""}
          onChangeText={(v) =>
            onChange({
              ...contact,
              primary_address: { ...contact?.primary_address, zip: v },
            })
          }
          style={{ width: 120 }}
        />
      </View>
    </View>
  );
}

export function DisplayNamesForm({
  displayNames,
  onChange,
  colors,
}: {
  displayNames?: z.infer<typeof DisplayNamesSchema>;
  onChange: (names: z.infer<typeof DisplayNamesSchema>) => void;
  colors: any;
}) {
  // Dynamically get all fields from the schema
  // Since DisplayNamesSchema has .default() applied, we need to access the inner type
  const schemaShape = (DisplayNamesSchema as any)._def.innerType.shape;
  const fields = Object.keys(schemaShape) as (keyof z.infer<typeof DisplayNamesSchema>)[];
  const defaults = getDisplayNameDefaults();

  const renderEntityFields = (field: keyof z.infer<typeof DisplayNamesSchema>) => {
    const value = displayNames?.[field] || { name: "", plural: "" };
    const defaultValue = defaults[field] || { name: "", plural: "" };

    // Extract description from the schema
    const fieldSchema = schemaShape[field];
    const description = fieldSchema._def?.description || "";

    // Convert field key to readable label (e.g., "time_cards" -> "Time Cards")
    const label = field
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return (
      <View key={field} style={[displayNameStyles.item, { borderBottomColor: colors.border }]}>
        <View style={displayNameStyles.labelSection}>
          <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
            {label}
          </ThemedText>
          {description && <ThemedText style={{ fontSize: 12, opacity: 0.5, marginTop: 6 }}>{description}</ThemedText>}
        </View>
        <View style={displayNameStyles.inputWrapper}>
          <ThemedInput
            value={value.name || ""}
            onChangeText={(v) => onChange({ ...defaults, ...displayNames, [field]: { ...value, name: v } })}
            placeholder={`e.g. ${defaultValue.name}`}
          />
          <ThemedText style={displayNameStyles.inputAnnotation}>Singular</ThemedText>
        </View>
        <View style={displayNameStyles.inputWrapper}>
          <ThemedInput
            value={value.plural || ""}
            onChangeText={(v) => onChange({ ...defaults, ...displayNames, [field]: { ...value, plural: v } })}
            placeholder={`e.g. ${defaultValue.plural}`}
          />
          <ThemedText style={displayNameStyles.inputAnnotation}>Plural</ThemedText>
        </View>
      </View>
    );
  };

  return <>{fields.map((field) => renderEntityFields(field))}</>;
}

const styles = StyleSheet.create({
  formGrid: {
    gap: 12,
  },
  row: {
    flexDirection: "row",
    gap: 12,
    alignItems: "flex-end",
  },
  card: {
    padding: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: "rgba(0,0,0,0.1)",
    borderRadius: 8,
    gap: 8,
  },
  fullWidth: {
    width: "100%",
  },
  iconBtn: {
    width: 40,
    height: 40,
    backgroundColor: "transparent",
  },
});

const displayNameStyles = StyleSheet.create({
  item: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  labelSection: {
    width: 200,
  },
  inputWrapper: {
    flex: 1,
    flexBasis: "30%",
    gap: 4,
  },
  inputAnnotation: {
    fontSize: 11,
    opacity: 0.45,
    marginLeft: 2,
  },
});
